import React, { useState, useEffect, useCallback } from 'react';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import AllianceCard from 'components/AllianceCard';
import Header from 'components/AppBar';
import Button from 'components/Buttons/Button';
import FieldWithoutGrid from 'components/FieldWithoutGrid';
import Input from 'components/Input';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Alliance, Loyalty } from 'services/loyaltiesService';
import { padLeadingZeros } from 'utils/generic';

import {
  AddContainer,
  Container,
  Content,
  FormContainer,
  CardContainer,
  CheckBoxContainer,
  CheckLabel,
  CheckBoxStyled,
  GroupButton,
} from './style';

interface Props {
  userId?: string;
  showAdd: boolean;
  isSubmitting: boolean;
  alliance: Alliance;
  onAddLoyalty: (loyalty: Loyalty) => void;
  onBack: () => void;
}

const AddLoyalty = ({ showAdd, isSubmitting, onBack, alliance, userId, onAddLoyalty }: Props) => {
  const { t } = useStoreTranslation();
  const [isChecked, setCheckedState] = useState(false);

  useEffect(() => {
    setCheckedState(false);
  }, [alliance]);

  const validationSchema = yup.object().shape({
    loyaltyNumber: yup.string().trim().max(100).required(),
  });

  const initValues = {
    userId,
    loyaltyId: alliance?.id || '',
    loyaltyNumber: '',
    useLoyaltyNumberIfSameAlliance: false,
  };

  const handleAddSubmit = useCallback(
    (value: FormikValues) => {
      onAddLoyalty({
        userId,
        loyaltyId: value.loyaltyId,
        loyaltyNumber: value.loyaltyId === 'AV' ? padLeadingZeros(value.loyaltyNumber, 11) : value.loyaltyNumber,
        useLoyaltyNumberIfSameAlliance: isChecked,
      } as Loyalty);
    },
    [isChecked, onAddLoyalty, userId],
  );

  const handleCheckBox = useCallback((isCheck: boolean) => {
    setCheckedState(isCheck);
  }, []);

  if (!showAdd) return null;

  return (
    <AddContainer>
      <Mobile>
        <Header goBack={onBack}>{t('travelerLoyalty.titleAddProgram')}</Header>
      </Mobile>

      <Desktop>
        <ModalHeader onClose={onBack} title={t('travelerLoyalty.titleAddProgram')} />
      </Desktop>

      <Container>
        <Content>
          <CardContainer>
            <AllianceCard
              allianceName={alliance?.name}
              logo={alliance?.logo}
              type={alliance?.type}
              initialColor={alliance?.color?.split(';')[0]}
              finalColor={alliance?.color?.split(';')[1]}
            />
          </CardContainer>
          <Formik
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={initValues}
            validationSchema={validationSchema}
            onSubmit={handleAddSubmit}
            isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
          >
            <FormContainer>
              <FieldWithoutGrid
                simpleTooltip
                simpleTooltipText={t('loyaltyForm.tooltipLoyaltyNumber')}
                name="loyaltyNumber"
                label={t('loyaltyForm.loyaltyNumberLabel')}
              >
                <Input autoComplete="off" placeholder={t('loyaltyForm.loyaltyNumber')} />
              </FieldWithoutGrid>
              {alliance.allianceName && (
                <CheckBoxContainer>
                  <CheckBoxStyled circle checked={isChecked} onChange={handleCheckBox}>
                    <CheckLabel>
                      {t('loyaltyForm.checkLabelAlliance', {
                        allianceName: alliance.allianceName,
                        name: alliance.name,
                      }).replace('&amp;', '&')}
                    </CheckLabel>
                  </CheckBoxStyled>
                </CheckBoxContainer>
              )}
              <GroupButton>
                <Button disabled={isSubmitting} loading={isSubmitting} value={t('loyaltyForm.confirmButton')} />
              </GroupButton>
            </FormContainer>
          </Formik>
        </Content>
      </Container>
    </AddContainer>
  );
};

export default AddLoyalty;
